import qs from 'query-string';

/**
 * Wrapper for the Query String library.
 * https://github.com/sindresorhus/query-string
 */

/**
 * Valid filter keys for rates and alt lending.
 */
const rules = {
  rates: {
    array: {
      lenders: true,
      lender_type: true,
      programs: true,
      term_id: true,
    },
    scalar: {
      amortization: true,
      beacon: true,
      gds: true,
      insurable: true,
      insurance: true,
      ltv: true,
      mortgage_amount: true,
      preferred: true,
      property_value: true,
      purchase: true,
      purpose: true,
      rate_hold: true,
      refinance: true,
      regions: true,
      sort: true,
      tds: true,
      transfer: true,
      type: true,
    },
  },
  altLending: {
    array: {
      lenders: true,
    },
    scalar: {
      amount: true,
      interest: true,
      loan_option: true,
      loan_type: true,
      location: true,
      location_other: true,
      location_rural: true,
      occupancy_type: true,
      owner_builder: true,
      owner_occupied: true,
      preferred: true,
      property_type: true,
      region: true,
      rental: true,
      serviced_by_municipality: true,
      serviced_by_well_septic: true,
      speculative: true,
      term: true,
      unserviced: true,
    },
  },
};

type RuleType = keyof typeof rules;

type FilterScalar = string | number | null;
export type FilterValue = FilterScalar | FilterScalar[];
type Filters = Record<string, FilterValue>;

/**
 * Returns true if the filter value has a valid key and type.
 */
const isValidFilter = (filter: string, type: RuleType, value: FilterValue) =>
  (filter in rules[type].array && Array.isArray(value)) || (filter in rules[type].scalar && !Array.isArray(value));

/**
 * Query String Configuration Options
 */
const options = { arrayFormat: 'bracket' } as const;

/**
 * Parse the query string to extract filters and whitelist valid values.
 */
const parse =
  (type: RuleType) =>
  (query: string, opts: qs.ParseOptions = options): Filters => {
    const filters = qs.parse(query, opts) as Filters;
    return Object.keys(filters).reduce<Filters>(
      (accum, filter) =>
        isValidFilter(filter, type, filters[filter]) ? { ...accum, [filter]: filters[filter] } : accum,
      {}
    );
  };

/**
 * Convert a filters object to string.
 */
const stringify = (query: Record<string, unknown>, opts: qs.StringifyOptions = options) => qs.stringify(query, opts);

export const ratesFilter = {
  parse: parse('rates'),
  stringify,
};

export const altLendingFilter = {
  parse: parse('altLending'),
  stringify,
};
